/**
 * What a row on the list says about itself beyond its name — and only when it is worth a line.
 *
 * Adding grants the maximum: read and write, automations on. So spelling out "Interactive: Read and
 * write · Automation: Read and write" on every row is the rule restated, in the column that has
 * least room for it. What earns the space is the EXCEPTION, in the words somebody outside this
 * codebase would use: not "Automation: Off" but "No automations".
 */
export interface RowAccess {
  interactive: 'read' | 'read_write';
  automation: 'none' | 'read' | 'read_write';
  /**
   * Whether the contents are in the workspace's search index. `undefined` means the question does
   * not apply — a site or a document library has no single item to register, so "not searchable"
   * would read as something somebody could switch on here.
   */
  searchable?: boolean;
}

/** The short notes a row shows, empty when it is at everything an add grants. */
export function accessNotes(access: RowAccess): string[] {
  const notes: string[] = [];
  if (access.interactive === 'read') notes.push('Read only');
  if (access.automation === 'none') notes.push('No automations');
  else if (access.automation === 'read') notes.push('Automations read only');
  if (access.searchable === false) notes.push('Not searchable');
  return notes;
}

/**
 * The way down to a row, as a breadcrumb, or null when it says nothing.
 *
 * One rendering for both connectors: `Site / Library / Reports` beside `python course`, rather than
 * a slash path on one tab and names on the other. Everything sits under the root, so a bare `/` is a
 * line that repeats what the reader already knows and is dropped.
 */
export function meaningfulPath(path: string | null | undefined): string | null {
  const parts = (path ?? '')
    .split('/')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  return parts.length > 0 ? parts.join(' / ') : null;
}

/**
 * The same breadcrumb read off a place's web URL, for a row stored before the path was.
 *
 * It is a fallback and reads like one — a URL carries site slugs and `%20` — so it is used only when
 * there is no stored path. Re-adding a place, or a snapshot refresh, replaces it with the names.
 */
export function pathFromWebUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const segments = parsed.pathname
    .split('/')
    .map((part) => {
      try {
        return decodeURIComponent(part);
      } catch {
        return part;
      }
    })
    .filter((part) => part.length > 0);
  /** Everything after the `/sites/` marker, and the last segment is the row's own name. */
  const start = segments.indexOf('sites');
  const trail =
    start === -1 ? segments : segments.slice(start + 1, segments.length);
  return trail.length > 1 ? trail.slice(0, -1).join(' / ') : null;
}
